import { AxiosError } from 'axios'
import isArray from 'lodash/isArray'
import orderBy from 'lodash/orderBy'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, OrderValues, SortDirection } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import { prepareGenericParams, setURLParams } from 'Core/utils'
import { storagePageSizeConstant } from 'Core/utils/storeHelpers/storagePageSizeConstant'

import { UrlParams } from 'Portals/Customer/constants'
import { Kpi, KpiMetricFilterFields, MetricByPeriods } from 'Portals/Customer/entities'
import { getFiltersConfig } from 'Portals/Customer/Pages/MetricByPeriods/FiltersWrapper/Filters/utils/config'
import { LocationRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { getMappedMetricsByPeriod } from 'Portals/Customer/utils'

const STORAGE_LOCATIONS_BY_PERIOD_PAGE_SIZE = storagePageSizeConstant('metricsByPeriod')

export class MetricByPeriodsTable extends TableStore<Kpi.MetricTermRow> {
	@observable accessor newItems: Kpi.MetricTermRow[] = []
	@observable accessor infinitePageNumber: number = 1

	constructor(private rootStore: RootStore) {
		super(STORAGE_LOCATIONS_BY_PERIOD_PAGE_SIZE)
	}

	@computed
	get isToCallGetItems() {
		return (
			this.filters[KpiMetricFilterFields.kpiCategoryMetric]?.data &&
			this.filters[KpiMetricFilterFields.kpiId]?.data &&
			this.filters[KpiMetricFilterFields.year]?.data
		)
	}

	@computed
	get kpiYear() {
		const year = this.filters[KpiMetricFilterFields.year]?.data
		const correctValue = isArray(year) ? year[0] : year

		return correctValue as number | undefined
	}

	@computed
	get hasKPIFilterValue(): boolean {
		return !!this.filters[KpiMetricFilterFields.kpiId]?.data
	}

	@computed
	get hasKPICategoryFilterValue(): boolean {
		return !!this.filters[KpiMetricFilterFields.kpiCategoryMetric]?.data
	}

	@action.bound
	onResortLocation(order: SortDirection | undefined, items: Kpi.MetricTermRow[]) {
		const correctOrder = order === SortDirection.ASC ? OrderValues.asc : OrderValues.desc
		this.items = orderBy(items, 'location.name', correctOrder)
	}

	@flow.bound
	*getItems(): CustomGenerator<ListData<MetricByPeriods.Basic>> {
		if (!this.isToCallGetItems) {
			return
		}

		setURLParams({ searchFilters: this.searchFilters, filters: this.filters })

		this.isLoading = true

		try {
			this.infinitePageNumber = 1
			const params = prepareGenericParams(this)
			const { kpiCategoryMetric, ...rest } = params

			const newParams = {
				sortOrder: SortDirection.DESC,
				sortField: 'id',
				...rest
			}

			const data = yield LocationRepo.getLocationMetricMeasureData(newParams)
			const mapped = getMappedMetricsByPeriod(data.items)
			this.totalCount = data.total
			this.items = mapped
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isLoading = false
		}
	}

	@flow.bound
	*addItems(): CustomGenerator<ListData<MetricByPeriods.Basic>> {
		const newPageNumber = this.infinitePageNumber + 1
		try {
			const params = prepareGenericParams(this)
			const { kpiCategoryMetric, ...rest } = params

			const newParams = {
				sortOrder: SortDirection.DESC,
				sortField: 'id',
				...rest,
				page: newPageNumber
			}

			const { items, total } = yield LocationRepo.getLocationMetricMeasureData(newParams)
			const mapped = getMappedMetricsByPeriod(items)
			this.totalCount = total || 0
			this.infinitePageNumber = newPageNumber
			this.items = [...this.items, ...mapped]
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@action.bound
	initFromURLParams(params: Record<string, string[]>) {
		const filterKeys = [UrlParams.kpiCategoryMetric, UrlParams.kpiId, UrlParams.year]
		this.initFilterParamsFromUrl(params, filterKeys, getFiltersConfig())
	}

	@action.bound
	clearTableConfig() {
		this.clearTableData()
		this.clearFilters()
		this.clearSorting()
	}

	@action.bound
	clearTableData() {
		this.items = []
		this.totalCount = 0
	}
}
